import { TCreateRSOReqDto, TCreateRSOResDto } from "@/api/services/admin/rso/dtos/create-match.dto";
import { TGetRSOListResDto } from "@/api/services/admin/rso/dtos/get-match-list.dto";
import { TGetRSOResDto } from "@/api/services/admin/rso/dtos/get-match.dto";
import { TSwitchRSOUserAsAdminResDto } from "@/api/services/admin/rso/dtos/switch-user-as-admin.dto";
import { TUpdateRSOReqDto, TUpdateRSOResDto } from "@/api/services/admin/rso/dtos/update-match.dto";
import { TDeleteRSOResDto } from "@/api/services/admin/rso/dtos/delete-rso.dto";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { usePagination } from "@/hooks/usePagination";
import { TApiRSOListPagReq } from "@/types/api";
import { ERSOContractStatus } from "@/types/enum";
import { clientApi } from "@/utils/api";
import { isNumber } from "@/utils/number";
import { createUrlWithParams } from "@/utils/url";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { TGetRSOUserListResDto, TRSOUserListReqDto } from "@/api/services/admin/rso/dtos/get-match-user-list.dto";

export const createRSOApi = async (payload: TCreateRSOReqDto): Promise<TCreateRSOResDto> => {
  const rs = await clientApi.post<TCreateRSOResDto>("/rsos", payload);
  return rs.data;
};

export const fetchRSOApi = async (rsoId: string): Promise<TGetRSOResDto> => {
  const rs = await clientApi.get<TGetRSOResDto>(`/rsos/${rsoId}`);
  return rs.data;
};

export const useRSO = () => {
  const { id } = useParams();

  return useQuery({
    queryKey: [QUERY_KEYS.RSO.FETCH_DETAIL, id],
    queryFn: () => (id ? fetchRSOApi(id) : undefined),
    enabled: Boolean(id && isNumber(id)),
  });
};

export const fetchRSOListApi = async (req: TApiRSOListPagReq): Promise<TGetRSOListResDto> => {
  const url = createUrlWithParams("/rsos", req);

  const rs = await clientApi.get<TGetRSOListResDto>(url);
  return rs.data;
};

export const fetchRSOUserListApi = async (rsoId?: string, req?: TRSOUserListReqDto) => {
  const url = createUrlWithParams(`rsos/${rsoId}/users`, req);
  const rs = await clientApi.get<TGetRSOUserListResDto>(url);
  return rs.data;
};

export const useRSOList = (contractStatuses?: ERSOContractStatus[]) => {
  const pagReq = usePagination();
  const params = {
    contractStatuses,
    ...pagReq,
  };
  return useQuery({
    queryKey: [QUERY_KEYS.RSO.FETCH_LIST, params],
    queryFn: () => fetchRSOListApi(params),
  });
};

export const useRSOUserList = (rsoId?: string, isUser?: boolean | null) => {
  const pagReq = usePagination();
  const params = {
    ...pagReq,
    ...(isUser !== undefined && isUser !== null && { isUser }),
  };
  return useQuery({
    queryKey: [QUERY_KEYS.RSO.FETCH_USER_LIST, params],
    queryFn: () => fetchRSOUserListApi(rsoId, params),
    enabled: Boolean(rsoId),
    gcTime: 0,
    staleTime: 0,
  });
};

export const setRSOAdminApi = async (rsoId: string, userId: number): Promise<TSwitchRSOUserAsAdminResDto> => {
  const rs = await clientApi.put<TSwitchRSOUserAsAdminResDto>(`/rsos/${rsoId}/users/${userId}/set-as-admin`);
  return rs.data;
};

export const updateRSOApi = async (rsoId: string, payload: TUpdateRSOReqDto): Promise<TUpdateRSOResDto> => {
  const rs = await clientApi.put<TUpdateRSOResDto>(`/rsos/${rsoId}`, payload);
  return rs.data;
};

export const deleteRSOApi = async (rsoId: string): Promise<TDeleteRSOResDto> => {
  const rs = await clientApi.delete<TDeleteRSOResDto>(`/rsos/${rsoId}`);
  return rs.data;
};

const getContractStatusCount = async () => {
  const rs = await clientApi.get("/rsos/get-contract-status-count");
  return rs.data;
};

export const useCountContractStatusCount = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.RSO.GET_CONTRACT_STATUS_COUNT],
    queryFn: () => getContractStatusCount(),
  });
};
